import React, { useState, lazy, Suspense, useEffect } from 'react';
import { Shield, Award, TrendingUp } from 'lucide-react';
import Modal from './Modal';
import { ComplianceService } from '../services/complianceService';

// Lazy load the KVKK pages and Compliance page
const KVKKAydinlatmaPage = lazy(() => import('../pages/KVKKAydinlatmaPage'));
const KVKKAcikRizaPage = lazy(() => import('../pages/KVKKAcikRizaPage'));
const CompliancePage = lazy(() => import('../pages/CompliancePage'));

const Footer: React.FC = () => {
  const [modalContent, setModalContent] = useState<{
    isOpen: boolean;
    title: string;
    content: 'aydinlatma' | 'acikRiza' | 'compliance' | null;
  }>({
    isOpen: false,
    title: '',
    content: null
  });
  
  const [complianceData, setComplianceData] = useState({ iso27001Score: 85, soc2Score: 78 });
  
  useEffect(() => {
    const loadComplianceData = async () => {
      const data = ComplianceService.generateComplianceReport();
      setComplianceData({
        iso27001Score: data.iso27001Score,
        soc2Score: data.soc2Score
      });
    };
    loadComplianceData();
  }, []);

  const openModal = (content: 'aydinlatma' | 'acikRiza' | 'compliance') => {
    const titles = {
      aydinlatma: 'KVKK Aydınlatma Metni',
      acikRiza: 'Açık Rıza Metni',
      compliance: 'Güvenlik Standartları ve Uyumluluk'
    };
    
    setModalContent({
      isOpen: true,
      title: titles[content],
      content
    });
  };

  const closeModal = () => {
    setModalContent({
      isOpen: false,
      title: '',
      content: null
    });
  };

  return (
    <>
      <footer className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 py-3 px-6 text-sm">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            {/* Sol taraf - Copyright */}
            <div className="text-gray-600">
              &copy; {new Date().getFullYear()} Ekostrateji. Tüm hakları saklıdır.
            </div>
            
            {/* Orta - Compliance Status Widget */}
            <button 
              onClick={() => openModal('compliance')}
              className="group flex items-center gap-3 px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:shadow-md hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
              title="Güvenlik sertifikasyon durumunu detaylı görüntüle"
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600 group-hover:text-blue-700" />
                <span className="font-medium text-gray-800 group-hover:text-blue-800 text-xs">Güvenlik Standartları</span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">ISO:</span>
                  <div className="w-8 bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-blue-600 h-1 rounded-full transition-all" 
                      style={{width: `${complianceData.iso27001Score}%`}}
                    />
                  </div>
                  <span className="text-xs font-medium text-blue-600">{complianceData.iso27001Score}%</span>
                </div>
                
                <div className="w-px h-4 bg-gray-300" />
                
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">SOC2:</span>
                  <div className="w-8 bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-green-600 h-1 rounded-full transition-all" 
                      style={{width: `${complianceData.soc2Score}%`}}
                    />
                  </div>
                  <span className="text-xs font-medium text-green-600">{complianceData.soc2Score}%</span>
                </div>
              </div>
            </button>
            
            {/* Sağ taraf - KVKK linkleri */}
            <div className="flex items-center space-x-4 text-gray-500">
              <button 
                onClick={() => openModal('aydinlatma')}
                className="hover:text-gray-700 hover:underline cursor-pointer transition-colors text-xs"
              >
                KVKK Aydınlatma Metni
              </button>
              <span>|</span>
              <button 
                onClick={() => openModal('acikRiza')}
                className="hover:text-gray-700 hover:underline cursor-pointer transition-colors text-xs"
              >
                Açık Rıza Metni
              </button>
            </div>
          </div>
        </div>
      </footer>

      <Modal 
        isOpen={modalContent.isOpen} 
        onClose={closeModal}
        title={modalContent.title}
        size="xl"
      >
        <Suspense fallback={<div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
          {modalContent.content === 'aydinlatma' && <KVKKAydinlatmaPage />}
          {modalContent.content === 'acikRiza' && <KVKKAcikRizaPage />}
          {modalContent.content === 'compliance' && <CompliancePage />}
        </Suspense>
      </Modal>
    </>
  );
};

export default Footer;
